<template>
  <div class="homepage">
    <!-- Hero -->
    <section class="hero">
      <h1 class="hero-title">Find a clinical trial that's right for you.</h1>
      <p class="hero-subtitle">
        Our Search Assistant can help you explore ongoing clinical trials, check if you may qualify,
        and connect you directly with researchers leading the study.  
      </p>
      <button
        type="button"
        class="hero-cta"
        @click="startSearch"
        aria-label="Start my search"
      >
        Start my search
      </button>
    </section>

    <!-- Hero image -->
    <div class="hero-image" role="img" aria-label="Clinical trials"></div>

    <!-- How it works -->
    <section id="how-it-works" class="how-it-works">
      <h2 class="section-title">How it works</h2>
      <div class="steps-grid">
        <article class="step-card">
          <div class="step-icon">
            <Search :size="56" :stroke-width="1.5" />
          </div>
          <div class="step-content">
            <h3>Tell us what you're looking for</h3>
            <p>Let us know which health area you are interested in finding clinical trials for.</p>
          </div>
        </article>
        <article class="step-card">
          <div class="step-icon">
            <ClipboardList :size="56" :stroke-width="1.5" />
          </div>
          <div class="step-content">
            <h3>Answer a few simple questions</h3>
            <p>Our Search Assistant will ask you some questions to see which study best fits your needs.  </p>
          </div>
        </article>
        <article class="step-card">
          <div class="step-icon">
            <CheckCircle2 :size="56" :stroke-width="1.5" />
          </div>
          <div class="step-content">
            <h3>Connect with the research team</h3>
            <p>We connect you directly with researchers to learn more and take the next step.</p>
          </div>
        </article>
      </div>
    </section>

    <!-- More confidence to get started -->
    <section class="confidence">
      <h2 class="section-title section-title--center">Benefits of We Search Research</h2>
      <div class="confidence-inner">
        <div class="confidence-image" role="img" aria-label="Person smiling outdoors"></div>
        <ul class="confidence-list">
          <li class="confidence-item">
            <h3>Free for patients</h3>
            <p>Search for clinical trials and connect with researchers at no cost.</p>
          </li>
          <li class="confidence-item">
            <h3>Secure and controlled access</h3>
            <p>The platform is securely hosted, and access to data is restricted to validated users.</p>
          </li>
          <li class="confidence-item">
            <h3>Information from trusted sources</h3>
            <p>The studies shown on the platform use data from partner researcher organizations or verified sources.</p>
          </li>
          <li class="confidence-item">
            <h3>You decide when to move forward</h3>
            <p>Your information is only shared when you choose to express interest in a study, with your consent.</p>
          </li>
        </ul>
      </div>
    </section>

    <!-- What you'll find here -->
    <section class="find-here">
      <header class="find-here-header">
        <h2 class="section-title">What you'll find here</h2>
        <p>
          Everything you need to explore clinical trials with more clarity,
          trusted information, and a simpler experience.
        </p>
      </header>
      <div class="find-grid">
        <article class="find-card">
          <h3>New and ongoing studies</h3>
          <p>Discover clinical trials that may be relevant to your needs, all in one place.</p>
        </article>
        <article class="find-card">
          <h3>Information from trusted sources</h3>
          <p>Explore studies with information from verified researcher organizations or reputable trial registries.</p>
        </article>
        <article class="find-card">
          <h3>The key details, made easier to understand</h3>
          <p>See the main participation details, study context, and location in a clearer and more accessible way.</p>
        </article>
        <article class="find-card">
          <h3>A simpler way to take the next step</h3>
          <p>If a study feels right for you, you can express interest and be connected with researchers.</p>
        </article>
      </div>
    </section>

    <!-- Partners band -->
    <section class="partners" aria-label="Our partners">
      <img
        class="partner-logo"
        src="https://placehold.co/200x80/5e9b92/ffffff?text=Hospitals"
        alt="Hospitals — partner network"
        width="200"
        height="80"
        loading="lazy"
        decoding="async"
      />
      <img
        class="partner-logo"
        src="https://placehold.co/200x80/5e9b92/ffffff?text=Clinics"
        alt="Clinics — partner network"
        width="200"
        height="80"
        loading="lazy"
        decoding="async"
      />
      <img
        class="partner-logo"
        src="https://placehold.co/200x80/5e9b92/ffffff?text=Researchers"
        alt="Research organizations — partner network"
        width="200"
        height="80"
        loading="lazy"
        decoding="async"
      />
      <img
        class="partner-logo"
        src="https://placehold.co/200x80/5e9b92/ffffff?text=Communities"
        alt="Patient communities — partner network"
        width="200"
        height="80"
        loading="lazy"
        decoding="async"
      />
    </section>

    <!-- FAQ teaser (full FAQ lives at /faq) -->
    <section class="faq-teaser" aria-label="Frequently asked questions">
      <h2 class="faq-teaser-title">Got questions?</h2>
      <p class="faq-teaser-text">
        We've answered the most common questions about clinical trials,
        how the platform works, safety, and what happens after you apply.
      </p>
      <router-link :to="faqRoute" class="faq-teaser-link">
        Read the FAQ →
      </router-link>
    </section>

    <!-- Login Modal -->
    <LoginModal
      v-if="showLogin"
      :prefill-email="prefilledEmail"
      :info-message="loginInfoMessage"
      @close="handleCloseLogin"
      @login-success="handleLoginSuccess"
      @show-register="handleShowRegister"
      @show-forgot-password="handleShowForgotPassword"
    />

    <!-- Registration Modal -->
    <RegisterModal
      v-if="showRegister"
      @close="showRegister = false"
      @register-success="handleRegisterSuccess"
      @show-login="handleShowLogin"
    />

    <!-- Forgot Password Modal -->
    <ForgotPasswordModal
      v-if="showForgotPassword"
      @close="showForgotPassword = false"
      @show-login="handleShowLogin"
    />
  </div>
</template>

<script>
import LoginModal from '@/components/modals/LoginModal.vue'
import RegisterModal from '@/components/modals/RegisterModal.vue'
import ForgotPasswordModal from '@/components/modals/ForgotPasswordModal.vue'
import { ROUTES } from '@/constants'
import { Search, ClipboardList, CheckCircle2 } from 'lucide-vue-next'
import { useSeo } from '@/composables/useSeo'

export default {
  name: 'HomePage',
  components: {
    LoginModal,
    RegisterModal,
    ForgotPasswordModal,
    Search,
    ClipboardList,
    CheckCircle2
  },
  setup() {
    useSeo({
      title: 'AI-powered clinical trial matching for patients',
      description: 'Discover clinical trials, check if you may qualify, and connect with research teams — through a free, secure AI search assistant.',
      type: 'website',
      canonical: '/'
    })
  },
  data() {
    return {
      showLogin: false,
      showRegister: false,
      showForgotPassword: false,
      prefilledEmail: '',
      loginInfoMessage: ''
    }
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated']
    },
    faqRoute() {
      return ROUTES.FAQ
    }
  },
  methods: {
    startSearch() {
      if (this.isAuthenticated) {
        this.$router.push(ROUTES.CHAT)
      } else {
        this.showLogin = true
      }
    },
    handleLoginSuccess() {
      this.showLogin = false
      this.prefilledEmail = ''
      this.loginInfoMessage = ''
      this.$router.push(ROUTES.WELCOME)
    },
    handleRegisterSuccess(payload) {
      // Registration succeeded but the user is NOT logged in yet — the
      // backend requires OTP verification on first login. Hand off to the
      // LoginModal with the email pre-filled so they only need to enter
      // their password to trigger the OTP email.
      this.showRegister = false
      this.prefilledEmail = payload?.email || ''
      this.loginInfoMessage =
        'Account created. Please sign in to receive your verification code.'
      this.showLogin = true
    },
    handleCloseLogin() {
      this.showLogin = false
      this.prefilledEmail = ''
      this.loginInfoMessage = ''
    },
    handleShowRegister() {
      this.showLogin = false
      this.loginInfoMessage = ''
      this.showRegister = true
    },
    handleShowLogin() {
      this.showRegister = false
      this.showForgotPassword = false
      this.showLogin = true
    },
    handleShowForgotPassword() {
      this.showLogin = false
      this.showRegister = false
      this.showForgotPassword = true
    },
    goToChat() {
      this.$router.push(ROUTES.CHAT)
    }
  }
}
</script>

<style scoped>
.homepage {
  background: #fef9f5;
  color: #0a1d14;
  min-height: 100vh;
}

/* ── Hero ── */
.hero {
  padding: 5rem 5rem 0;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.15;
  color: #0a1d14;
  max-width: 56rem;
  margin: 0;
}

.hero-subtitle {
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.2;
  color: rgba(16, 55, 47, 0.6);
  max-width: 53rem;
  margin: 0;
}

.hero-cta {
  margin-top: 0.5rem;
  background: #10372f;
  color: #faf9f7;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.15px;
  line-height: 1.5;
  padding: 1rem 2rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  font-family: inherit;
}

.hero-cta:hover {
  background: #0c2d24;
  transform: translateY(-1px);
}

/* ── Hero image ── */
.hero-image {
  margin: 2.5rem 5rem 0;
  border-radius: 16px;
  overflow: hidden;
  height: 26rem;
  background:
    url('https://picsum.photos/id/924/1400/400') center 25% / cover no-repeat,
    #cbd5e1;
}

/* ── How it works ── */
.how-it-works {
  background: rgba(242, 238, 235, 0.5);
  padding: 7.5rem 5rem;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.section-title {
  font-size: 3rem;
  font-weight: 600;
  color: #10372f;
  margin: 0;
  line-height: 1.15;
}

.section-title--center {
  text-align: center;
  color: #0a1d14;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
}

.step-card {
  background: #fbfaf8;
  border-radius: 16px;
  padding: 3rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 2.25rem;
}

.step-icon {
  width: 4.5rem;
  height: 4.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #10372f;
}

.step-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.step-content h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10372f;
  margin: 0;
}

.step-content p {
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.2;
  color: rgba(16, 55, 47, 0.6);
  margin: 0;
}

/* ── More confidence ── */
.confidence {
  padding: 5rem;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
  align-items: center;
}

.confidence-inner {
  display: flex;
  gap: 5rem;
  align-items: center;
  width: 100%;
  max-width: 1280px;
}

.confidence-image {
  width: 39.25rem;
  height: 38.4rem;
  flex-shrink: 0;
  border-radius: 16px;
  overflow: hidden;
  background:
    url('https://picsum.photos/id/401/400/400') center top / cover no-repeat,
    #cbd5e1;
}

.confidence-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 3rem;
  list-style: none;
  margin: 0;
  padding: 0;
}

.confidence-item {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.confidence-item h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #0a1d14;
  line-height: 1.2;
  margin: 0;
}

.confidence-item p {
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.2;
  color: rgba(16, 55, 47, 0.6);
  margin: 0;
}

/* ── What you'll find here ── */
.find-here {
  padding: 0 5rem 5rem;
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.find-here-header {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.find-here-header p {
  font-size: 1.5rem;
  font-weight: 400;
  line-height: 1.2;
  color: rgba(16, 55, 47, 0.6);
  max-width: 53rem;
  margin: 0;
}

.find-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
}

.find-card {
  background: rgba(94, 155, 146, 0.1);
  border-radius: 16px;
  padding: 2.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.find-card h3 {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10372f;
  margin: 0;
}

.find-card p {
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.2;
  color: rgba(16, 55, 47, 0.6);
  margin: 0;
}

/* ── Partners band ── */
.partners {
  background: #5e9b92;
  height: 12.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5rem;
  padding: 0 5rem;
  flex-wrap: wrap;
}

.partner-logo {
  height: 80px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.12);
  filter: brightness(1.05);
  opacity: 0.95;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.partner-logo:hover {
  opacity: 1;
  transform: translateY(-2px);
}

/* ── FAQ teaser ── */
.faq-teaser {
  background: #10372f;
  padding: 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
}

.faq-teaser-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #faf9f7;
  margin: 0;
}

.faq-teaser-text {
  font-size: 1.125rem;
  color: #cfd7d5;
  max-width: 48rem;
  margin: 0;
  line-height: 1.5;
}

.faq-teaser-link {
  margin-top: 1rem;
  display: inline-block;
  background: #faf9f7;
  color: #10372f;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.875rem 1.75rem;
  border-radius: 9999px;
  text-decoration: none;
  transition: transform 0.15s ease, background 0.15s ease;
}

.faq-teaser-link:hover {
  background: #ffffff;
  transform: translateY(-1px);
}

/* ── Responsive ── */
@media (max-width: 1200px) {
  .hero-title { font-size: 3rem; }
  .hero-subtitle,
  .find-here-header p,
  .confidence-item p { font-size: 1.25rem; }
  .section-title { font-size: 2.5rem; }
  .faq-teaser-title { font-size: 2rem; }
  .confidence-image { width: 28rem; height: 28rem; }
  .confidence-inner { gap: 3rem; }
}

@media (max-width: 960px) {
  .hero { padding: 3.5rem 1.5rem 0; }
  .hero-title { font-size: 2.5rem; }
  .hero-subtitle { font-size: 1.125rem; }
  .hero-image { margin: 2rem 1.5rem 0; height: 18rem; }

  .how-it-works,
  .confidence,
  .find-here,
  .faq-teaser { padding: 4rem 1.5rem; }
  .find-here { padding-top: 0; }

  .section-title { font-size: 2rem; }
  .faq-teaser-title { font-size: 1.75rem; }
  .steps-grid { grid-template-columns: 1fr; }
  .step-card { padding: 2rem 1.5rem; gap: 1.5rem; }
  .step-content h3,
  .find-card h3,
  .confidence-item h3 { font-size: 1.25rem; }
  .step-content p,
  .find-card p { font-size: 1rem; }

  .confidence-inner { flex-direction: column; gap: 2rem; }
  .confidence-image { width: 100%; height: 18rem; }
  .confidence-list { gap: 2rem; }
  .confidence-item p { font-size: 1.0625rem; }

  .find-grid { grid-template-columns: 1fr; }
  .find-card { padding: 1.75rem 1.5rem; }

  .partners {
    height: auto;
    padding: 2.5rem 1.5rem;
    gap: 1.75rem;
  }

  .faq-teaser-text { font-size: 1rem; }
}

@media (max-width: 480px) {
  .hero-title { font-size: 2rem; }
  .section-title { font-size: 1.75rem; }
  .faq-teaser-title { font-size: 1.5rem; }
}
</style>

